import type { PayloadAction } from '@reduxjs/toolkit';
import type { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { EDSFiltersRequestBody } from '@/features/eds/types/api/requests/EDSFiltersRequestBody';
import type { RootState } from '@/store/types/RootState';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { EdsFilterEntities } from '@/features/eds/enums/EdsFilterEntities';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { ClaimantSlots } from '@/domains/claimants/enums/ClaimantSlots';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import RecordService from '@/services/RecordService/RecordService';
import {
  getClaimantsList,
  getClaimantsListSuccess,
  incrementClaimantsListPage,
  setClaimantsListLoadingStatus,
} from '../store/claimantsListSlice';

function* getClaimantsListSaga({ type, payload: { filters } }: PayloadAction<EDSFiltersRequestBody>) {
  try {
    const claimId =
      (filters.find((filterItem) => filterItem.propertyName === EdsFilterEntities.ClaimId)?.values[0] as string) ?? '';

    const pagination = yield select(
      (state: RootState) => state.claimantsList.claimantsListByClaimId[claimId]?.pagination,
    );

    yield put(setClaimantsListLoadingStatus(true));

    const requestUrl = `/filter/${EdsEntityCodes.Claimant}?${QueryParamsService.constructSearchParamsFromRecord({
      page: pagination?.page ?? 1,
      perPage: pagination?.perPage ?? 20,
      includes: [EdsEntityCodes.Milestone, EdsEntityCodes.HelperTags],
    })}`;

    const { data } = yield call(HttpService.post, requestUrl, filters ?? [], {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      includeId: true,
    });

    const claimantsWithoutAvailableHelperTags = normalizedResponse.map((normalizedClaimant) => {
      return {
        ...normalizedClaimant,
        entities: {
          ...RecordService.omit([EdsEntityCodes.AvailableHelperTags], normalizedClaimant.entities),
        },
      };
    });

    EdsDebugService.logResponse(EdsEntityCodes.Claimant, {
      rawResponse: data,
      normalizedResponse,
      dataBySlot: normalizedResponse.length
        ? EdsAdapterService.generateSlotRecord<ClaimantSlots>(normalizedResponse[0])
        : {},
    });

    yield put(
      getClaimantsListSuccess({
        shouldRewrite: type !== incrementClaimantsListPage.type,
        claimId,
        data: { listings: claimantsWithoutAvailableHelperTags, pagination: data.pagination },
      }),
    );
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setClaimantsListLoadingStatus(false));
  }
}

export function* claimantsWatcher() {
  yield takeEvery(getClaimantsList.type, getClaimantsListSaga);
}
